const SUBSTANCES_SAVE_DELAY = 2000
const PRODUCT_CARD_SAVE_DELAY = 1500

const getService = (context, name) => {
    const service = context.services && context.services[name]

    if (typeof service !== 'function') {
        throw new Error(`Concern substance lists requires services.${name}()`)
    }

    return service
}

const getRequestLegislationListIds = context => (
    ((context.request && context.request.legislations) || [])
        .map(legislation => legislation.substanceList && legislation.substanceList.id)
        .filter(id => id !== null && typeof id !== 'undefined')
)

const createConcernSubstanceListsController = ({
    context,
    step,
    setDirty,
    setSaving,
    publishEffect,
    reportError,
    substancesSaveDelay = SUBSTANCES_SAVE_DELAY,
    productCardSaveDelay = PRODUCT_CARD_SAVE_DELAY,
}) => {
    let substancesTimerId = null
    let productCardTimerId = null
    let pendingSubstancesSave = null
    let pendingProductCardSave = null
    let substancesDirty = false
    let productCardDirty = false
    let substancesRevision = 0
    let productCardRevision = 0
    let activeSaveOperations = 0
    let disposed = false

    const syncDirty = () => setDirty(substancesDirty || productCardDirty)

    const beginSaveOperation = () => {
        activeSaveOperations += 1

        if (activeSaveOperations === 1) {
            setSaving(true)
        }
    }

    const endSaveOperation = () => {
        activeSaveOperations -= 1

        if (activeSaveOperations === 0) {
            setSaving(false)
        }
    }

    const publish = effect => Promise.resolve()
        .then(() => publishEffect(effect))
        .catch(reportError)

    const saveSubstances = () => {
        if (pendingSubstancesSave) {
            return pendingSubstancesSave
        }

        const revision = substancesRevision
        beginSaveOperation()
        pendingSubstancesSave = Promise.resolve()
            .then(() => getService(context, 'saveSubstances')())
            .then(() => publishEffect({
                type: 'product.concern-substances.changed',
                phase: 'committed',
                scopes: ['product.concern-substances'],
            }))
            .then(() => {
                if (revision === substancesRevision) {
                    substancesDirty = false
                    syncDirty()
                }
                reportError(null)
            })
            .catch(error => {
                reportError(error)
                throw error
            })
            .finally(() => {
                pendingSubstancesSave = null
                endSaveOperation()
            })

        return pendingSubstancesSave
    }

    const saveProductCard = () => {
        if (pendingProductCardSave) {
            return pendingProductCardSave
        }

        const revision = productCardRevision
        beginSaveOperation()
        pendingProductCardSave = Promise.resolve()
            .then(() => getService(context, 'saveProductCard')())
            .then(() => publishEffect({
                type: 'product.card.changed',
                phase: 'committed',
                scopes: ['product.card'],
            }))
            .then(() => {
                if (revision === productCardRevision) {
                    productCardDirty = false
                    syncDirty()
                }
                reportError(null)
            })
            .catch(error => {
                reportError(error)
                throw error
            })
            .finally(() => {
                pendingProductCardSave = null
                endSaveOperation()
            })

        return pendingProductCardSave
    }

    const scheduleSubstancesSave = () => {
        clearTimeout(substancesTimerId)
        substancesTimerId = setTimeout(() => {
            substancesTimerId = null
            saveSubstances().catch(() => undefined)
        }, substancesSaveDelay)
    }

    const scheduleProductCardSave = () => {
        clearTimeout(productCardTimerId)
        productCardTimerId = setTimeout(() => {
            productCardTimerId = null
            saveProductCard().catch(() => undefined)
        }, productCardSaveDelay)
    }

    const markSubstancesChanged = () => {
        substancesDirty = true
        substancesRevision += 1
        syncDirty()
        publish({
            type: 'product.concern-substances.changed',
            phase: 'draft',
            scopes: ['product.concern-substances'],
        })
        scheduleSubstancesSave()
    }

    const markProductCardChanged = () => {
        productCardDirty = true
        productCardRevision += 1
        syncDirty()
        publish({
            type: 'product.card.changed',
            phase: 'draft',
            scopes: ['product.card'],
        })
        scheduleProductCardSave()
    }

    const resolveIncludeListIds = emittedListIds => {
        if (step.settings.includeListIdsMode === 'emitted-list-ids' && emittedListIds.length > 0) {
            return emittedListIds
        }

        return getRequestLegislationListIds(context)
    }

    const requirePayloadValue = (action, key) => {
        if (!action.payload || !action.payload[key]) {
            throw new Error(`Concern substance lists action "${action.type}" requires payload.${key}`)
        }

        return action.payload[key]
    }

    return Object.freeze({
        validate() {
            return Promise.resolve({ isValid: true, errors: [] })
        },
        async handleAction(action) {
            if (disposed) {
                return
            }

            switch (action.type) {
            case 'substance.include': {
                const substance = requirePayloadValue(action, 'substance')
                const emittedListIds = Array.isArray(action.payload.emittedListIds)
                    ? action.payload.emittedListIds
                    : []
                await getService(context, 'includeConcernSubstance')(substance, resolveIncludeListIds(emittedListIds))
                markSubstancesChanged()
                return
            }
            case 'substance.exclude':
                await getService(context, 'excludeConcernSubstance')(requirePayloadValue(action, 'substance'))
                markSubstancesChanged()
                return
            case 'substance.undefined':
                await getService(context, 'makeConcernSubstanceUndefined')(requirePayloadValue(action, 'substance'))
                markSubstancesChanged()
                return
            case 'list-question.update':
                await getService(context, 'updateListQuestionState')(
                    requirePayloadValue(action, 'list'),
                    action.payload.state,
                )
                markProductCardChanged()
                return
            case 'information-owner-settings.update':
                await getService(context, 'updateInformationOwnerSettings')(requirePayloadValue(action, 'settings'))
                markProductCardChanged()
                return
            default:
                throw new Error(`Unsupported concern substance lists action "${action.type}"`)
            }
        },
        async flush() {
            clearTimeout(substancesTimerId)
            clearTimeout(productCardTimerId)
            substancesTimerId = null
            productCardTimerId = null

            while (substancesDirty || productCardDirty) {
                if (substancesDirty) {
                    // The channels share product state and completion must flush them deterministically.
                    // eslint-disable-next-line no-await-in-loop
                    await saveSubstances()
                }

                if (productCardDirty) {
                    // eslint-disable-next-line no-await-in-loop
                    await saveProductCard()
                }
            }
        },
        dispose() {
            disposed = true
            clearTimeout(substancesTimerId)
            clearTimeout(productCardTimerId)
            substancesTimerId = null
            productCardTimerId = null
        },
    })
}

export {
    SUBSTANCES_SAVE_DELAY,
    PRODUCT_CARD_SAVE_DELAY,
    createConcernSubstanceListsController,
}
export default createConcernSubstanceListsController
